import { useState } from "react";
import { useTranslation } from "react-i18next";

import { faEdit, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Modal } from "@/components/Modals/Modal";
import { Spinner } from "@/components/Spinner/Spinner";

import { useDeleteTaskTemplateMutation } from "../../../redux/task-template/task-template.api";
import { TaskTemplateInfo } from "../schema/task-template";

interface TaskTemplateTableProps {
	templates: TaskTemplateInfo[];
	isLoading: boolean;
	onEdit: (template: TaskTemplateInfo) => void;
}

export const TaskTemplateTable = ({ templates, isLoading, onEdit }: TaskTemplateTableProps) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`manage-task-templates.${key}`);

	const [deleteTemplate] = useDeleteTaskTemplateMutation();
	const [deleteConfirm, setDeleteConfirm] = useState<TaskTemplateInfo | null>(null);

	const handleDelete = async () => {
		if (deleteConfirm?.id) {
			await deleteTemplate(deleteConfirm.id);
			setDeleteConfirm(null);
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<Spinner />
			</div>
		);
	}

	if (!templates.length) {
		return (
			<div className="text-center py-12 text-primary-gray-lighter">
				{ts("list-empty")}
			</div>
		);
	}

	return (
		<>
			<div className="overflow-x-auto rounded-xl border border-gray-200">
				<table className="w-full text-sm text-left">
					<thead className="bg-gray-50 text-primary-gray font-semibold">
						<tr>
							<th className="px-4 py-3">{ts("table.title")}</th>
							<th className="px-4 py-3">{ts("table.priority")}</th>
							<th className="px-4 py-3">{ts("table.order")}</th>
							<th className="px-4 py-3">{ts("table.subtasks")}</th>
							<th className="px-4 py-3 text-right">{ts("table.actions")}</th>
						</tr>
					</thead>
					<tbody>
						{templates.map((template) => (
							<tr
								key={template.id}
								className="border-t border-gray-200 hover:bg-gray-50 transition-colors"
							>
								<td className="px-4 py-3 font-medium text-primary-gray">
									{template.title}
								</td>
								<td className="px-4 py-3 text-primary-gray-lighter">
									{template.priority || "—"}
								</td>
								<td className="px-4 py-3 text-primary-gray-lighter">
									{template.order ?? "—"}
								</td>
								<td className="px-4 py-3 text-primary-gray-lighter">
									{template.subTaskTemplates?.length || 0}
								</td>
								<td className="px-4 py-3">
									<div className="flex items-center justify-end gap-2">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											image={<Icon icon={faEdit} />}
											size={ButtonSize.XS}
											title=""
											type="button"
											onClick={() => onEdit(template)}
										/>
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											image={<Icon icon={faTrash} />}
											size={ButtonSize.XS}
											title=""
											type="button"
											onClick={() => setDeleteConfirm(template)}
										/>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<Modal
				handleClose={() => setDeleteConfirm(null)}
				handleSave={handleDelete}
				isOpened={!!deleteConfirm}
				submitButtonText={ts("delete-confirm")}
				title={ts("delete-title")}
			>
				<p className="text-primary-gray text-sm">
					{ts("delete-description").replace("{{title}}", deleteConfirm?.title || "")}
				</p>
			</Modal>
		</>
	);
};

